import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Stack,
    LinearProgress,
    Tabs,
    Tab,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    LocationOn,
    Language,
    CalendarToday,
    Verified,
    Block,
    Schedule,
    Category,
    Feedback,
    TrendingUp,
    EmojiEvents,
    Star,
    ThumbUp,
    ThumbDown,
    AccessTime,
    Timeline,
    Analytics,
    Assignment,
    Close
} from '@mui/icons-material';
import { getUserDetails } from '../../../api/admin';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`user-details-tabpanel-${index}`}
            aria-labelledby={`user-details-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

// User Basic Info Component
const UserBasicInfo = ({ user }) => {
    const getRoleColor = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return '#9c27b0';
            case 'ADMIN': return '#f44336';
            case 'USER': return '#4caf50';
            default: return '#757575';
        }
    };

    const getStatusColor = (isActive) => isActive ? '#4caf50' : '#f44336';

    // Safely handle undefined user object
    if (!user) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography>Loading user information...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" spacing={3} alignItems="center">
                    <Avatar
                        src={user.profilePhotoPath}
                        sx={{ width: 80, height: 80, fontSize: '2rem' }}
                    >
                        {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>

                    <Stack spacing={1} sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight="bold">
                            {user.displayName || 'Unknown User'}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                                label={user.role || 'USER'}
                                size="small"
                                sx={{
                                    backgroundColor: getRoleColor(user.role),
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            />
                            <Chip
                                label={user.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                    backgroundColor: getStatusColor(user.isActive),
                                    color: 'white'
                                }}
                                icon={user.isActive ? <Verified /> : <Block />}
                            />
                            {user.isVerified && (
                                <Chip
                                    label="Email Verified"
                                    size="small"
                                    color="success"
                                    icon={<Verified />}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

// Contact Information Component
const ContactInfo = ({ user }) => {
    // Safely handle undefined user object
    if (!user) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography>Loading contact information...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ mr: 1 }} />
                    Contact Information
                </Typography>

                <List dense>
                    <ListItem>
                        <ListItemIcon><Email /></ListItemIcon>
                        <ListItemText primary="Email" secondary={user.email || 'N/A'} />
                    </ListItem>

                    {user.phoneNumber && (
                        <ListItem>
                            <ListItemIcon><Phone /></ListItemIcon>
                            <ListItemText primary="Phone" secondary={user.phoneNumber} />
                        </ListItem>
                    )}

                    {user.location && (
                        <ListItem>
                            <ListItemIcon><LocationOn /></ListItemIcon>
                            <ListItemText primary="Location" secondary={user.location} />
                        </ListItem>
                    )}

                    <ListItem>
                        <ListItemIcon><Language /></ListItemIcon>
                        <ListItemText primary="Language" secondary={user.preferredLanguage || 'English'} />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon><CalendarToday /></ListItemIcon>
                        <ListItemText
                            primary="Member Since"
                            secondary={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        />
                    </ListItem>

                    {user.lastLoginAt && (
                        <ListItem>
                            <ListItemIcon><AccessTime /></ListItemIcon>
                            <ListItemText
                                primary="Last Login"
                                secondary={new Date(user.lastLoginAt).toLocaleString()}
                            />
                        </ListItem>
                    )}
                </List>
            </CardContent>
        </Card>
    );
};

// Activity Statistics Component
const ActivityStats = ({ stats }) => (
    <Card sx={{ mb: 2 }}>
        <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Analytics sx={{ mr: 1 }} />
                Activity Statistics
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Category sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {stats?.totalClassifications || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Classifications
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Feedback sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {stats?.totalFeedbacks || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Feedback Given
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <EmojiEvents sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {stats?.rewardsPoints || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Points
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Star sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {stats?.totalBadges || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Badges Earned
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {stats?.averageAccuracy !== undefined && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Performance Metrics
                    </Typography>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Average Classification Accuracy
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={stats.averageAccuracy * 100}
                            sx={{ height: 10, borderRadius: 5, mb: 1 }}
                        />
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            {(stats.averageAccuracy * 100).toFixed(1)}%
                        </Typography>
                    </Paper>
                </Box>
            )}
        </CardContent>
    </Card>
);

// Recent Classifications Component
const RecentClassifications = ({ classifications }) => (
    <Card>
        <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assignment sx={{ mr: 1 }} />
                Recent Classifications
            </Typography>

            {classifications && classifications.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Confidence</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {classifications.slice(0, 10).map((classification, index) => (
                                <TableRow key={classification.id || index}>
                                    <TableCell>
                                        {new Date(classification.classifiedAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={classification.categoryName || 'Unknown'}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(classification.resultConfidence || 0, 100)}
                                                sx={{ minWidth: 60, height: 6, borderRadius: 3 }}
                                            />
                                            <Typography variant="body2" fontWeight="bold">
                                                {(classification.resultConfidence || 0).toFixed(1)}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={classification.isCorrected ? 'Corrected' : 'Original'}
                                            size="small"
                                            color={classification.isCorrected ? 'warning' : 'success'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info">No classifications found</Alert>
            )}
        </CardContent>
    </Card>
);

// Recent Feedback Component
const RecentFeedback = ({ feedbacks }) => (
    <Card>
        <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Feedback sx={{ mr: 1 }} />
                Recent Feedback
            </Typography>

            {feedbacks && feedbacks.length > 0 ? (
                <Stack spacing={2}>
                    {feedbacks.slice(0, 5).map((feedback, index) => (
                        <Paper key={index} sx={{ p: 2 }} variant="outlined">
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={feedback.type}
                                        size="small"
                                        color={feedback.type === 'BUG_REPORT' ? 'error' : 'primary'}
                                    />
                                    <Chip
                                        label={feedback.status}
                                        size="small"
                                        color={feedback.status === 'RESOLVED' ? 'success' : 'warning'}
                                    />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(feedback.createdAt).toLocaleDateString()}
                                </Typography>
                            </Stack>

                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {feedback.message}
                            </Typography>

                            {feedback.response && (
                                <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Admin Response:
                                    </Typography>
                                    <Typography variant="body2">
                                        {feedback.response}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    ))}
                </Stack>
            ) : (
                <Alert severity="info">No feedback found</Alert>
            )}
        </CardContent>
    </Card>
);

// Main User Details Dialog Component
const UserDetailsDialog = ({ open, onClose, userId }) => {
    const [tabValue, setTabValue] = useState(0);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const fetchUserDetails = async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await getUserDetails(userId);
            // The API returns the UserDetailDTO directly, not nested under data.data
            setUserDetails(response.data);
        } catch (err) {
            setError('Failed to fetch user details');
            console.error('Error fetching user details:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && userId) {
            fetchUserDetails();
        }
    }, [open, userId]);

    const handleClose = () => {
        setTabValue(0);
        setUserDetails(null);
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { height: '90vh' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">
                    User Details
                </Typography>
                <Tooltip title="Close">
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}

                {userDetails && !loading && (
                    <>
                        <Box sx={{ px: 3, pt: 2 }}>
                            <UserBasicInfo user={userDetails} />
                        </Box>

                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
                        >
                            <Tab label="Contact Info" />
                            <Tab label="Activity Stats" />
                            <Tab label="Classifications" />
                            <Tab label="Feedback" />
                        </Tabs>

                        <TabPanel value={tabValue} index={0}>
                            <ContactInfo user={userDetails} />
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <ActivityStats stats={userDetails} />
                        </TabPanel>

                        <TabPanel value={tabValue} index={2}>
                            <RecentClassifications classifications={userDetails.recentClassifications} />
                        </TabPanel>

                        <TabPanel value={tabValue} index={3}>
                            <RecentFeedback feedbacks={userDetails.recentFeedbacks} />
                        </TabPanel>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserDetailsDialog;
